// Component style overrides
import { extendTheme } from '@chakra-ui/react'

import { buttonStyles } from './components/button'
import { drawerStyles } from './components/drawer'
import { formLabelStyles } from './components/form-label'
import { inputStyles } from './components/input'
import { switchStyles } from './components/switch'
import { tabStyles } from './components/tab'

export const SIDE_BAR_WIDTH_CLOSED = '60px'
export const SIDE_BAR_WIDTH_OPEN = '240px'
export const CALL_CONTAINER_Z_INDEX = 1401

const fonts = {
  heading: 'Euclid Square',
  body: 'Euclid Square',
}

const colors = {
  // Colour reference to Figma
  'darkblue-050': '#E6EAED',
  'darkblue-100': '#B3BFC6',
  'darkblue-200': '#80949F',
  'darkblue-300': '#64748B',
  'darkblue-400': '#264B5B',
  'darkblue-500': '#002233',
  'darkblue-600': '#001E2D',
  'darkblue-700': '#001824',
  'darkblue-800': '#00121B',
  'darkblue-900': '#000A0F',
  'duskblue-700': '#0C3145',
  'duskblue-800': '#092738',
  'signalyellow-300': '#E6FF81',
  'signalyellow-400': '#DDFE55',
  'signalyellow-500': '#D4FF29',
  'signalyellow-800': '#7A9400',
  'signalyellow-900': '#576B00',
  'checkbox-selected': {
    500: '#092738',
  },

  brand: {
    50: '#3369A8',
    100: '#2E6CDB',
    150: '#7FABFF',
    175: '#a9bbfa',
    200: '#6096F5',
    250: '#004492',
    300: '#e4effb',
    400: '#2D2E41',
    500: '#353849',
    600: '#8E919F',
    700: '#1CFDE5',
    800: '#07DB94',
    900: '#3279C7',

    /* region NEW Brand Colours */
    primary: {
      light: '#14B8A6',
      dark: '#DDFE55',
    },
    glow: {
      light: null,
      dark: 'Dawn Blue 400',
    },
    dark: {
      light: '1A2538',
      dark: 'Dark Blue 900',
    },
    overwatch: {
      light: 'E8F0FE',
      dark: 'TBD',
    },
  },
  icon: {
    primary: {
      light: '#FFFFFF',
      dark: '#E6EAED',
    },
    secondary: {
      light: '#94A3B8',
      dark: '#64748B',
    },
    active: {
      light: '#14B8A6',
      dark: '#DDFE55',
    },
    danger: {
      light: '#EF4444',
      dark: 'Red 400',
    },
    'on-accent': {
      light: '#FFFFFF',
      dark: 'Dark Blue 900',
    },
    disabled: {
      light: '#D1D5DB',
      dark: 'Ghost Protocol 600',
    },
    topbar: {
      light: '#475569',
      dark: '#E6EAED',
    },
    breadcrumb: {
      light: '#979595',
      dark: '#FFFFFF',
    },
  },
  /* region NEW Shadow Colours */
  shadow: {
    card: {
      light: '0 1px 3px rgba(0,0,0,0.1)',
      dark: '0 4px 16px rgba(0,0,0,0.3)',
    },
    elevated: {
      light: '0 4px 12px rgba(0,0,0,0.15)',
      dark: '0 8px 32px rgba(0,0,0,0.5)',
    },
    glow: {
      light: null,
      dark: 'Dawn Blue 400 (15% opacity / 20px blur)',
    },
    dropdown: {
      light: '0 2px 8px rgba(0,0,0,0.12)',
      dark: '0 4px 12px rgba(0,0,0,0.4)',
    },
    /* endregion */
  },
  bg: {
    100: '#F0F5FD',
    200: '#C9DBF8',
    300: '#DCE8FA',
    400: '#F8E5E8',
    500: '#F3F3F3',
    550: '#EEF4FC',
    600: '#66666E',

    /* region NEW Bg Colours */
    app: {
      light: '#EFF1F5',
      dark: 'Dark Blue 900',
    },
    sidebar: {
      light: '#E2E8F0',
      // Dark Blue 500
      dark: '#002233',
    },
    'sidebar-hover': {
      light: '#1E2F45',
      dark: '#001E2D',
    },
    'sidebar-active': {
      light: '#1C283F',
      dark: 'transparent',
    },
    card: {
      light: '#F0F1F5',
      dark: '#00121B',
    },
    'card-elevated': {
      light: '#FFFFFF',
      dark: '#001824',
    },
    'card-elevated-secondary': {
      light: '#F0F1F5',
      dark: '#001E2D',
    },
    input: {
      light: '#FFFFFF',
      dark: '#092738',
    },
    'table-row': {
      light: '#FFFFFF',
      dark: 'Dark Blue 900',
    },
    'table-row-alt': {
      light: '#F8F9FC',
      dark: '#00121B',
    },
    'table-row-hover': {
      light: '#EDF2F7',
      dark: '#00121B',
    },
    overlay: {
      light: 'rgba(0,0,0,0.4)',
      dark: 'Black (60% opacity)',
    },
    topbar: {
      light: '#FFFFFF',
      dark: '#001824',
    },
    chip: {
      light: '#374151',
      dark: '#DDFE55',
    },
    'chip-label': {
      light: '#FFFFFF',
      dark: '#264B5B',
    },
    'alert-info': {
      light: '#EBF5FF',
      dark: 'Dusk Blue 900',
    },
    'alert-success': {
      light: '#ECFDF5',
      dark: 'Green 900',
    },
    'alert-danger': {
      light: '#7F1D1D',
      dark: 'Red 900',
    },
    'toggle-track': {
      light: '#D1D5DB',
      dark: 'Ghost Protocol 700',
    },
    'sidebar-account': {
      light: '#9DABC8',
      dark: '#264B5B',
    },
    'user-badge': {
      light: '#31405E',
      dark: 'Dawn Blue 300',
    },
    'tab-active': {
      light: '#FFFFFF',
      dark: '#264B5B',
    },
    divider: {
      light: '#E2E8F0',
      dark: '#264B5B',
    },
    'divider-dark': {
      light: '#E2E8F0',
      dark: '#001E2D',
    },
    disabled: {
      light: '#F3F4F6',
      dark: '#00121B',
    },
    'filters-btn': {
      light: '#14B8A6',
      dark: '#00121B',
    },
    /* endregion */
  },
  btn: {
    100: '#548BEF',
    200: '#96ABD2',
  },
  border: {
    100: '#E2E8F0',
    200: '#DBDBDB',
    300: '#BBB',
    400: '#FBC4C3',
    500: '#E9E9EB',
    /* region NEW Border Colours */
    default: {
      light: '#E2E8F0',
      dark: 'Ghost Protocol 800',
    },
    subtle: {
      light: '#F1F5F9',
      dark: 'Ghost Protocol 800',
    },
    strong: {
      light: '#CBD5E1',
      dark: 'Ghost Protocol 600',
    },
    glow: {
      light: null,
      dark: 'Dawn Blue 400',
    },
    input: {
      light: '#D1D5DB',
      dark: 'Ghost Protocol 700',
    },
    'input-hover': {
      light: '#CBD5E1',
      dark: '#0C3145',
    },
    'input-focus': {
      light: '#14B8A6',
      dark: '#264B5B',
    },
    'input-error': {
      light: '#DC2626',
      dark: 'Red 600',
    },
    error: {
      light: '#DC2626',
      dark: 'Red 500',
    },
    chip: {
      light: '#D1D5DB',
      dark: 'Dawn Blue 600',
    },
    search: {
      light: '#E2E8F0',
      dark: 'Ghost Protocol 700',
    },
    topbar: {
      light: '#E2E8F0',
      dark: '#002233',
    },
    'tab-toggle': {
      light: '#D1D5DB',
      dark: '#264B5B',
    },
    'card-elevated-secondary': {
      light: '#E2E8F0',
      dark: '#264B5B',
    },
    /* endregion */
  },
  link: { 100: '#3D7FCE' },
  incident: {
    active: {
      100: '#D01030',
      200: '#b40e2a',
    },
    progress: {
      100: '#D16A1E',
    },
    resolved: {
      100: '#29D11E',
    },
  },
  components: {
    switch: {
      active: '#2fb2b0',
      inactive: '#931100',
    },
    tab: {
      active: '#005BBF',
    },
  },
  success: {
    100: '#009a0d',
  },
  alert: {
    100: '#fd0e0a',
  },
  sidebar: {
    darkText: '#091848',
    avatarBg: '#495870',
  },
  /* region NEW Text Colours */
  text: {
    primary: {
      light: '#1E293B',
      dark: '#E6EAED',
    },
    secondary: {
      light: '#64748B',
      dark: '#4D6978',
    },
    header: {
      light: '#264B5B',
      dark: '#4D6978',
    },
    tertiary: {
      light: '#94A3B8',
      dark: 'Ghost Protocol 500',
    },
    'input-value': {
      light: '#374151',
      dark: '#80949F',
    },
    // 'input-placeholder': {
    //   light: '#374151',
    //   dark: '#80949F',
    // },
    'input-disabled': {
      light: '#9CA3AF',
      dark: '#264B5B',
    },
    disabled: {
      light: '#CBD5E1',
      dark: 'Ghost Protocol 600',
    },
    'on-accent': {
      light: '#FFFFFF',
      dark: 'Dark Blue 900',
    },
    'on-danger': {
      light: '#FFFFFF',
      dark: '#E6EAED',
    },
    link: {
      light: '#0891B2',
      dark: 'Dawn Blue 300',
    },
    'badge-count': {
      light: '#FFFFFF',
      dark: '#E6EAED',
    },
    'chip-label': {
      light: '#E8EDF2',
      dark: 'Dark Blue 900',
    },
    'alert-info': {
      light: '#1E40AF',
      dark: 'Ghost Protocol 300',
    },
    'alert-info-bold': {
      light: '#1E3A5A',
      dark: '#E6EAED',
    },
    'alert-success': {
      light: '#166534',
      dark: 'Green 200',
    },
    'alert-success-bold': {
      light: '#14532D',
      dark: 'Green 100',
    },
    'alert-danger': {
      light: '#FFFFFF',
      dark: 'Red 200',
    },
    'alert-danger-bold': {
      light: '#FFFFFF',
      dark: 'Red 300',
    },
    'nav-default': {
      light: '#FFFFFF',
      dark: '#64748B',
    },
    'nav-active': {
      light: '#FFFFFF',
      dark: '#DDFE55',
    },
    'page-title': {
      light: '#1E293B',
      dark: '#E6EAED',
    },
    'count-badge': {
      light: '#FFFFFF',
      dark: '#002233',
    },
    'chip-label-secondary': {
      light: '#64748B',
      dark: '#B3BFC6',
    },
    'btn-primary': {
      light: '#FFFFFF',
      dark: '#002233',
    },
  },
  /* endregion */
  /* region NEW Interactive Colours */
  interactive: {
    primary: {
      light: '#14B8A6',
      dark: '#DDFE55',
    },
    'primary-hover': {
      light: '#0D9488',
      dark: 'Signal Yellow 300',
    },
    'primary-active': {
      light: '#0F766E',
      dark: 'Signal Yellow 500',
    },
    'primary-disabled-bg': {
      light: '#99F6E4',
      dark: 'Signal Yellow 900',
    },
    'primary-disabled-text': {
      light: '#5EEAD4',
      dark: 'Signal Yellow 800',
    },
    danger: {
      light: '#DC2626',
      dark: 'Red 500',
    },
    'danger-hover': {
      light: '#EF4444',
      dark: 'Red 400',
    },
    'danger-active': {
      light: '#B91C1C',
      dark: 'Red 600',
    },
    'danger-disabled-bg': {
      light: '#FCA5A5',
      dark: 'Red 900',
    },
    'danger-disabled-text': {
      light: '#F87171',
      dark: 'Red 800',
    },
    'secondary-border': {
      light: '#D1D5DB',
      dark: '#E6EAED',
    },
    'secondary-text': {
      light: '#374151',
      dark: '#E6EAED',
    },
    'secondary-hover-bg': {
      light: 'transparent',
      dark: 'Dark Blue 700 (10% opacity)',
    },
    'secondary-hover-border': {
      light: '#9CA3AF',
      dark: 'Ghost Protocol 300',
    },
    'secondary-active-bg': {
      light: 'transparent',
      dark: 'Dark Blue 600 (15% opacity)',
    },
    'secondary-active-border': {
      light: '#6B7280',
      dark: 'Ghost Protocol 400',
    },
    'secondary-active-text': {
      light: '#1F2937',
      dark: '#E6EAED',
    },
    'secondary-disabled-border': {
      light: '#E5E7EB',
      dark: 'Ghost Protocol 700',
    },
    'secondary-disabled-text': {
      light: '#D1D5DB',
      dark: 'Ghost Protocol 600',
    },
    'tertiary-border': {
      light: '#E2E8F0',
      dark: '#0C3145',
    },
    'tertiary-text': {
      light: '#64748B',
      dark: 'Ghost Protocol 400',
    },
    'tertiary-hover-bg': {
      light: 'transparent',
      dark: 'Dusk Blue 800 (10% opacity)',
    },
    'tertiary-hover-border': {
      light: '#CBD5E1',
      dark: 'Dusk Blue 600',
    },
    'tertiary-active-bg': {
      light: 'transparent',
      dark: 'Dusk Blue 700 (15% opacity)',
    },
    'tertiary-active-border': {
      light: '#9CA3AF',
      dark: 'Dusk Blue 500',
    },
    'tertiary-active-text': {
      light: '#475569',
      dark: 'Ghost Protocol 300',
    },
    'tertiary-disabled-border': {
      light: '#F1F5F9',
      dark: '#092738',
    },
    'tertiary-disabled-text': {
      light: '#CBD5E1',
      dark: 'Ghost Protocol 700',
    },
    'focus-ring': {
      light: '#14B8A6',
      dark: 'Dawn Blue 400',
    },
    'toggle-on': {
      light: '#14B8A6',
      dark: '#DDFE55',
    },
    'nav-active': {
      light: null,
      dark: '#DDFE55',
    },
    'selected-row': {
      light: '#EBF5FF',
      dark: 'Dawn Blue 900',
    },
  },
  /* region NEW Status Colours */
  status: {
    success: {
      light: '#22C55E',
      dark: 'Green 500',
    },
    'success-bg': {
      light: '#DCFCE7',
      dark: 'Green 900',
    },
    'success-icon': {
      light: '#16A34A',
      dark: 'Green 300',
    },
    warning: {
      light: '#F59E0B',
      dark: 'Yellow 500',
    },
    'warning-bg': {
      light: '#FEF3C7',
      dark: 'Yellow 900',
    },
    error: {
      light: '#DC2626',
      dark: 'Red 500',
    },
    'error-bg': {
      light: '#FEE2E2',
      dark: 'Red 900',
    },
    info: {
      light: '#3B82F6',
      dark: 'Dawn Blue 400',
    },
    'info-bg': {
      light: '#DBEAFE',
      dark: 'Dusk Blue 900',
    },
    'info-icon': {
      light: '#2563EB',
      dark: 'Dusk Blue 500',
    },
    neutral: {
      light: '#6B7280',
      dark: 'Ghost Protocol 500',
    },
    'neutral-bg': {
      light: '#F3F4F6',
      dark: 'Ghost Protocol 800',
    },
    'incident-badge': {
      light: '#FFFFFF',
      dark: '#E6EAED',
    },
    'incident-badge-topbar': {
      light: '#1E3A5A',
      dark: '#DDFE55',
    },
    'guard-badge': {
      light: '#14B8A6',
      dark: 'TBD',
    },
    'sop-operator': {
      light: '#1E3A5A',
      dark: 'TBD',
    },
    'sop-guard': {
      light: '#14B8A6',
      dark: 'TBD',
    },
  },
}

const components = {
  Tooltip: {
    baseStyle: {
      borderRadius: 'md',
    },
  },
  Button: buttonStyles,
  Input: inputStyles,
  Drawer: drawerStyles,
  FormLabel: formLabelStyles,
  Switch: switchStyles,
  Modal: {
    parts: ['overlay', 'item'],
    baseStyle: {
      overlay: {
        background: 'rgba(48,56,78,0.7)',
      },
    },
  },
  Skeleton: {
    baseStyle: {
      borderRadius: 'md',
    },
  },
  Tabs: tabStyles,
}

const shadows = {
  outline: 'none',
}

export const theme = extendTheme({
  shadows,
  colors,
  fonts,
  components,
})
